import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import axios from 'axios';
import { ACTASAPI, EVALUACIONAPI } from '../../../../const';
import { useSelector } from 'react-redux';
import { isFulfilledAction, isPendingAction, isRejectedAction } from '../../../../utils/redux';

type State = {
  postulacionesPuesto: PostulacionesPuesto[];
  postulantes: PostulantesPuestosDetalle | null;
  error: string;
  success: string;
  loading: boolean;
};
type Requisitos = {
  declaraCumplir: boolean;
  descripcion: string;
  excluyente: boolean;
  idRequisito: number;
  idRiquisitoDeclarado: number;
  nombre: string;
};
type Postulantes = {
  apellido: string;
  dni: string;
  etaparechazo: string;
  idPostulantePuesto: number;
  idUsuario: number;
  nombre: string;
  rechazado: boolean;
  procesado: boolean;
  requisitosDeclarados: Requisitos[];
};
type PostulantesPuestosDetalle = {
  ubicacion: string;
  agrupamiento: string;
  capacidadTerciaria: boolean;
  funcionEspecifica: boolean;
  reserva: boolean;
  denominacion: string;
  idPuesto: number;
  nivel: string;
  requerido: number;
  postulantesAceptados: Postulantes[];
  postulantesRechazados: Postulantes[];
};
type PostulacionesPuesto = {
  ubicacion: string;
  agrupamiento: string;
  capacidadTerciaria: boolean;
  funcionEspecifica: boolean;
  reserva: boolean;
  denominacion: string;
  idPuesto: number;
  nivel: string;
  requerido: number;
  cantidadPostulantesAceptados: number;
  cantidadPostulantesRechazados: number;
};

const initialState: State = {
  postulacionesPuesto: [],
  postulantes: null,
  error: '',
  loading: false,
  success: '',
};

const sliceName = 'postulacionesPuesto';

type GetPostulacionesPuestosValues = { idLlamadoConcurso: number };
export const getPostulacionesPuesto = createAsyncThunk<
  PostulacionesPuesto[],
  GetPostulacionesPuestosValues
>(`${sliceName}/getDetalleConcurso`, async ({ idLlamadoConcurso }) => {
  const response = await axios.get(
    `${EVALUACIONAPI}/postulaciones/detalle-concurso/${idLlamadoConcurso}`,
  );
  const data = response.data as any[];

  return data;
});

type GetPuestoValue = { idPuesto: number };
export const getPuesto = createAsyncThunk<PostulantesPuestosDetalle, GetPuestoValue>(
  `${sliceName}/getDetallePostulante`,
  async ({ idPuesto }) => {
    const response = await axios.get(
      `${EVALUACIONAPI}/postulaciones/detalle-concurso/postulantes/${idPuesto}`,
    );

    const data = response.data;

    return data;
  },
);

type GetReporteEstadoPostulantesValues = {
  idLlamadoConcurso: number;
  idEtapaLlamadoConcurso: number;
};
export const getReporteEstadoConcurso = createAsyncThunk<void, GetReporteEstadoPostulantesValues>(
  `${sliceName}/getReporteEstadoConcurso`,
  async ({ idLlamadoConcurso, idEtapaLlamadoConcurso }) => {
    const response = await axios.get(
      `${ACTASAPI}/reportes-evaluaciones/estado-postulantes/${idLlamadoConcurso}/${idEtapaLlamadoConcurso}`,
      {
        responseType: 'blob',
      },
    );
    const data = response.data;
    const file = new Blob([data], { type: 'application/pdf' });
    const url = (window.URL ? URL : webkitURL).createObjectURL(file);
    window.open(url);
  },
);

type RequestReporteValidacionDocumental = {
  idLlamadoConcurso: number;
};
export const getReporteValidacionDocumental = createAsyncThunk<
  void,
  RequestReporteValidacionDocumental
>(`${sliceName}/getReporteValidacionDocumental`, async ({ idLlamadoConcurso }) => {
  const response = await axios.get(
    `${ACTASAPI}/reportes-evaluaciones/evaluacion-documental/estado/aceptado-excluido/${idLlamadoConcurso}`,
    {
      responseType: 'blob',
    },
  );
  const data = response.data;
  const file = new Blob([data], { type: 'application/pdf' });
  const url = (window.URL ? URL : webkitURL).createObjectURL(file);
  window.open(url);
});

type RequestReporteRequisitosDeclarados = {
  idLlamadoConcurso: number;
};
export const getReporteRequisitosDeclarados = createAsyncThunk<
  void,
  RequestReporteRequisitosDeclarados
>(`${sliceName}/getReporteRequisitosDeclarados`, async ({ idLlamadoConcurso }) => {
  const response = await axios.get(
    `${ACTASAPI}/reportes-inscripciones/llamado-concurso/${idLlamadoConcurso}/detalles-requisitos-inscripcion`,
    {
      responseType: 'blob',
    },
  );
  const data = response.data;
  const file = new Blob([data], { type: 'application/pdf' });
  const url = (window.URL ? URL : webkitURL).createObjectURL(file);
  window.open(url);
});

const postulacionesPuestoSlice = createSlice({
  name: sliceName,
  initialState,
  reducers: {
    cleanMsg: (state) => ({ ...state, success: '', error: '' }),
    cleanState: () => initialState,
    cleanFile: (state) => ({ ...state, file: '', bases: '' }),
  },
  extraReducers: (builder) => {
    builder.addCase(getPostulacionesPuesto.fulfilled, (state, action) => {
      state.postulacionesPuesto = action.payload;
    });
    builder.addCase(getPuesto.fulfilled, (state, action) => {
      state.postulantes = action.payload;
    });
    builder.addCase(getReporteRequisitosDeclarados.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(getReporteRequisitosDeclarados.fulfilled, (state) => {
      state.loading = false;
    });
    builder.addCase(getReporteValidacionDocumental.fulfilled, (state) => {
      state.loading = false;
    });
    builder.addCase(getReporteValidacionDocumental.pending, (state) => {
      state.loading = true;
    });
    builder.addMatcher(isRejectedAction(sliceName), (state, action) => {
      state.loading = false;
      // if (action.type.includes('bases')) return;
      state.error = 'No se pudo obtener la información.';
    });

    builder.addMatcher(isPendingAction(sliceName), (state) => {
      state.loading = true;
    });

    builder.addMatcher(isFulfilledAction(sliceName), (state) => {
      state.loading = false;
      state.success = 'Se obtuvo la información con éxito';
    });
  },
});

export const usePostulacionesPuesto = () =>
  useSelector((state: any) => state.postulacionesPuesto) as State;
export const { cleanMsg, cleanState, cleanFile } = postulacionesPuestoSlice.actions;
const postulacionesPuestoReducer = postulacionesPuestoSlice.reducer;
export default postulacionesPuestoReducer;
